import { IModule, ModuleContent, ModuleParams } from './types';
import { PdfRect, PdfSize, ETextAlign, PdfFont, PdfBrush, PageGen } from '../pdf/pdf';

export class ModuleRegistry {
  private modules = new Map<string, IModule>();

  register(module: IModule): void {
    if (this.modules.has(module.name)) {
      return;
    }
    this.modules.set(module.name, module);
  }

  get(name: string): IModule | undefined {
    return this.modules.get(name);
  }

  has(name: string): boolean {
    return this.modules.has(name);
  }

  getAll(): IModule[] {
    return Array.from(this.modules.values());
  }

  unregister(name: string): boolean {
    return this.modules.delete(name);
  }

  clear(): void {
    this.modules.clear();
  }
}

export const moduleRegistry = new ModuleRegistry();

export function parseModuleValue(value: string): {
  type: 'string' | 'module';
  value: string | ModuleContent;
} {
  const trimmed = value.trim();

  if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
    return parseModuleTag(trimmed);
  }

  return { type: 'string', value: trimmed };
}

function parseModuleTag(tagString: string): {
  type: 'string' | 'module';
  value: string | ModuleContent;
} {
  // Find the end of the opening tag (first '>' not inside quotes)
  let tagEnd = -1;
  let inQuotes = false;
  let quoteChar = '';

  for (let i = 1; i < tagString.length; i++) {
    const ch = tagString[i];

    if (inQuotes) {
      if (ch === quoteChar) {
        inQuotes = false;
      }
    } else {
      if (ch === '"' || ch === "'") {
        inQuotes = true;
        quoteChar = ch;
      } else if (ch === '>') {
        tagEnd = i;
        break;
      }
    }
  }

  if (tagEnd === -1) {
    return { type: 'string', value: tagString };
  }

  const openingTag = tagString.slice(1, tagEnd);
  const rest = tagString.slice(tagEnd + 1);

  // Parse opening tag: name followed by optional attributes
  const nameMatch = /^\s*(\w+)(.*)$/.exec(openingTag);
  if (!nameMatch) {
    return { type: 'string', value: tagString };
  }

  const name = nameMatch[1];
  const attrsStr = nameMatch[2].trim();

  // Check if it's self-closing (ends with /)
  const isSelfClosing = attrsStr.endsWith('/');

  if (isSelfClosing) {
    // Self-closing tag: <name attr="value"/>
    const attrs = parseAttributes(attrsStr.slice(0, -1));
    return parseModuleContent(name, '', attrs);
  }

  // Find closing tag
  const closeTag = `</${name}>`;
  const closePos = rest.lastIndexOf(closeTag);

  if (closePos === -1) {
    return { type: 'string', value: tagString };
  }

  const content = rest.slice(0, closePos);
  const attrs = parseAttributes(attrsStr);

  return parseModuleContent(name, content, attrs);
}

function parseModuleContent(
  name: string,
  content: string,
  attrs: Record<string, string>
): { type: 'string' | 'module'; value: string | ModuleContent } {
  const module = moduleRegistry.get(name);

  if (!module) {
    return { type: 'string', value: `<${name}>${content}</${name}>` };
  }

  const parsed = module.parse(content, attrs);
  return { type: 'module', value: parsed };
}

function parseAttributes(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  if (!attrString.trim()) return attrs;

  const regex = /(\w+)=(?:"([^"]*)"|'([^']*)')/g;
  let match;

  while ((match = regex.exec(attrString)) !== null) {
    attrs[match[1]] = match[2] || match[3] || '';
  }

  return attrs;
}
